import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";

import { useUsuario } from "../../Hooks/UsuarioHook";
import { ehCpfDeManutencao } from "../../Utils/permissoes";
import { Api } from "../../Utils/Api";

/**
 * Faixa de MANUTENÇÃO — liberação da equipe Sinsoft.
 *
 * Cria a conta de cada pessoa da equipe em toda entidade onde faltar, marca
 * administrador + servidor e vincula todos os sistemas. Quem faz o trabalho é o
 * backend (POST /manutencao/liberar-equipe), numa passada só.
 *
 * ⚠️ Some para quem não é da manutenção — mas isso é só conveniência. A TRAVA
 * de verdade é no servidor, pelo CPF do token (UNICO_LOGIN/Utils/cpfsSinsoft.ts).
 * Esconder botão não protege rota.
 *
 * Fica na HOME de propósito: antes estava dentro do modal "Acessar outra conta"
 * e ninguém achava — ação de manutenção não pode depender de lembrar em qual
 * diálogo ela mora.
 */
export const LiberarEquipeSinsoft = () => {
  const { usuario } = useUsuario();

  const [confirmar, setConfirmar] = useState(false);
  const [liberando, setLiberando] = useState(false);
  const [resultado, setResultado] = useState<{ ok: boolean; mensagem: string } | null>(null);

  if (!ehCpfDeManutencao(usuario?.usuario?.cpf)) return null;

  const liberar = async () => {
    setConfirmar(false);
    setLiberando(true);
    setResultado(null);

    try {
      const { data } = await Api.post(`/manutencao/liberar-equipe`);
      setResultado({ ok: !!data.status, mensagem: data.mensagem });
    } catch (e: any) {
      setResultado({
        ok: false,
        mensagem:
          e?.response?.data?.mensagem ??
          "Falha ao liberar a equipe. Confira se o backend foi reiniciado.",
      });
    } finally {
      setLiberando(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          mb: 2,
          px: 1.75,
          py: 1.25,
          borderRadius: 2,
          border: 1,
          borderColor: "warning.light",
          bgcolor: (t) =>
            t.palette.mode === "dark" ? "rgba(255,167,38,0.08)" : "rgba(255,167,38,0.10)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <BuildRoundedIcon sx={{ fontSize: 18, color: "warning.main" }} />

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 800, lineHeight: 1.2 }}>
            Manutenção Sinsoft
          </Typography>
          <Typography
            variant="caption"
            color={resultado && !resultado.ok ? "error" : "text.secondary"}
            sx={{ fontSize: "0.73rem" }}
          >
            {resultado
              ? resultado.mensagem
              : "Cria a conta da equipe em toda entidade que faltar, marca administrador e servidor, e vincula todos os sistemas."}
          </Typography>
        </Box>

        <Button
          size="small"
          color="warning"
          variant="contained"
          disabled={liberando}
          startIcon={
            liberando ? <CircularProgress size={14} color="inherit" /> : <GroupAddIcon />
          }
          onClick={() => setConfirmar(true)}
          sx={{ borderRadius: 2, textTransform: "none", whiteSpace: "nowrap" }}
        >
          {liberando ? "Liberando…" : "Liberar equipe"}
        </Button>
      </Box>

      <Dialog open={confirmar} onClose={() => setConfirmar(false)}>
        <DialogTitle>Liberar toda a equipe Sinsoft?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Cria a conta de <strong>cada pessoa da equipe</strong> em toda entidade onde
            ela ainda não tem, marca <strong>administrador</strong> e{" "}
            <strong>servidor</strong>, e vincula <strong>todos os sistemas</strong>.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
            São dezenas de municípios. Fica registrado em auditoria e pode ser repetido
            sem duplicar — mas não se desfaz sozinho.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(false)}>Cancelar</Button>
          <Button color="warning" variant="contained" onClick={liberar}>
            Liberar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
